import { useEffect, useState } from "react";
import { api, fmtMoney } from "@/lib/api";
import { useEntity, GROUP_VALUE } from "@/lib/entityStore";
import { Card } from "@/components/ds/Card";
import { PageShell, PageHeader, Section, Loading } from "@/components/ds/PageShell";
import { Stat } from "@/components/ds/Stat";
import { Confidence } from "@/components/ds/Severity";
import {
    LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer,
    CartesianGrid, Legend, ReferenceLine,
} from "recharts";

export default function Forecast() {
    const { selectedEntityId } = useEntity();
    const [data, setData] = useState(null);

    useEffect(() => {
        const q = selectedEntityId === GROUP_VALUE ? "" : `&entity_id=${selectedEntityId}`;
        api.get(`/forecast?weeks=13${q}`).then((r) => setData(r.data));
    }, [selectedEntityId]);

    if (!data)
        return (
            <PageShell>
                <Loading label="Loading forecast…" testId="forecast-loading" />
            </PageShell>
        );

    const isGroup = !!data.group_series;
    const series = isGroup
        ? data.group_series
        : data.scenarios.expected_case.points.map((p, i) => ({
              week_starting: p.week_starting,
              expected: p.ending_cash,
              stress: data.scenarios.stress_case.points[i].ending_cash,
              best: data.scenarios.best_case.points[i].ending_cash,
          }));
    const summary = data.summary;
    const confidence = isGroup ? "medium" : data.confidence;

    const stressMin = summary.stress_min_cash;
    const stressHeadline =
        stressMin < 0
            ? <>Stress scenario projects cash going negative — low of <span className="text-red-700">{fmtMoney(stressMin)}</span>.</>
            : stressMin < 100_000
              ? <>Stress scenario stays positive but tight (low <span className="text-amber-700">{fmtMoney(stressMin)}</span>).</>
              : <>Stress scenario remains comfortably positive (low <span className="text-emerald-700">{fmtMoney(stressMin)}</span>).</>;

    const stressDetail =
        stressMin < 0
            ? "The business would need either a credit facility or accelerated collections to absorb a downturn."
            : stressMin < 100_000
              ? "A safety buffer is advisable."
              : "No immediate liquidity concern under the modelled downturn.";

    return (
        <PageShell width="default" testId="forecast-page">
            <PageHeader
                eyebrow="13-week cashflow forecast"
                title="Forecast"
                subtitle="Rolling 13-week projection from historical baseline, open AR/AP timing, payroll cadence and BAS estimate. Confidence degrades visibly when source data is thin."
            />

            <Card variant="default" padding="lg" className="mb-6" testId="forecast-summary">
                <div className="flex items-center gap-2 label-soft mb-2 text-stone-500">
                    <span>Outlook</span>
                    <span>·</span>
                    <Confidence level={confidence} />
                </div>
                <div className="text-display-md text-stone-900 leading-snug mb-2">
                    {stressHeadline}
                </div>
                <p className="text-body text-stone-500 max-w-prose">{stressDetail}</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-x-8 gap-y-5 mt-6">
                    <Stat label="Expected · ending" value={summary.expected_ending_cash} size="sm" testId="kpi-fc-exp-end" />
                    <Stat label="Expected · min" value={summary.expected_min_cash} size="sm" testId="kpi-fc-exp-min" />
                    <Stat label="Stress · ending" value={summary.stress_ending_cash} size="sm" tone={summary.stress_ending_cash >= 0 ? "default" : "negative"} testId="kpi-fc-str-end" />
                    <Stat label="Stress · min" value={stressMin} size="sm" tone={stressMin >= 0 ? "default" : "negative"} testId="kpi-fc-str-min" />
                    <Stat label="Best · ending" value={summary.best_ending_cash} size="sm" tone="positive" testId="kpi-fc-best-end" />
                </div>
            </Card>

            <Section
                title={isGroup ? "Group cash projection" : `${data.entity_name} cash projection`}
                subtitle="Best, expected and stress scenarios over 13 weeks."
            >
                <Card variant="default" padding="lg">
                    <div style={{ width: "100%", height: 320, minHeight: 320 }}>
                        <ResponsiveContainer>
                            <LineChart data={series}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" />
                                <XAxis dataKey="week_starting" stroke="#78716c" fontSize={10} tickFormatter={(d) => d.slice(5)} />
                                <YAxis stroke="#78716c" fontSize={10} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                                <Tooltip formatter={(v) => fmtMoney(v)} contentStyle={{ borderRadius: 6, fontSize: 12 }} />
                                <Legend wrapperStyle={{ fontSize: 11 }} />
                                <ReferenceLine y={0} stroke="#dc2626" strokeDasharray="3 3" />
                                <Line type="monotone" dataKey="best" stroke="#10b981" dot={false} strokeWidth={1.5} />
                                <Line type="monotone" dataKey="expected" stroke="#a12a65" dot={false} strokeWidth={2} />
                                <Line type="monotone" dataKey="stress" stroke="#dc2626" dot={false} strokeWidth={1.5} />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                </Card>
            </Section>

            {isGroup && (
                <Section title="Per-entity 13-week outlook">
                    <Card variant="default" padding="none" className="overflow-hidden">
                        <table className="w-full text-[14px]">
                            <thead>
                                <tr className="text-left border-b border-stone-100 bg-stone-50/40">
                                    <th className="px-5 py-3 label-soft text-stone-500 font-medium">Entity</th>
                                    <th className="px-3 py-3 label-soft text-stone-500 font-medium">Confidence</th>
                                    <th className="px-3 py-3 label-soft text-stone-500 font-medium text-right">Expected end</th>
                                    <th className="px-3 py-3 label-soft text-stone-500 font-medium text-right">Expected min</th>
                                    <th className="px-3 py-3 label-soft text-stone-500 font-medium text-right">Stress min</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.per_entity.map((p) => (
                                    <tr key={p.entity_id} className="border-b border-stone-100 last:border-0">
                                        <td className="px-5 py-3.5 text-stone-800">{p.entity_name}</td>
                                        <td className="px-3 py-3.5">
                                            <Confidence level={p.confidence} />
                                        </td>
                                        <td className="px-3 py-3.5 font-mono-num text-right text-stone-700">{fmtMoney(p.summary.expected_ending_cash)}</td>
                                        <td className="px-3 py-3.5 font-mono-num text-right text-stone-700">{fmtMoney(p.summary.expected_min_cash)}</td>
                                        <td className={`px-3 py-3.5 font-mono-num text-right ${p.summary.stress_min_cash < 0 ? "text-red-700" : "text-stone-700"}`}>
                                            {fmtMoney(p.summary.stress_min_cash)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </Card>
                </Section>
            )}
        </PageShell>
    );
}
